import clsx from "clsx";
import {
  PERSONA_INITIALS,
  PERSONA_LABELS,
  type GuardianType,
  type RatingLevel,
} from "@/services/api/guardiansv2/types";
import { PERSONA_DOT_CLASS } from "./constants";

export interface PersonaAvatarProps {
  persona: GuardianType;
  rating?: RatingLevel | null;
  score?: number | null;
  skipped?: boolean;
  size?: "xs" | "sm";
}

function personaTitle(
  persona: GuardianType,
  options: { skipped: boolean; rating?: RatingLevel | null; score?: number | null }
): string {
  if (options.skipped) {
    return `${PERSONA_LABELS[persona]} skipped`;
  }
  if (options.score != null) {
    return `${PERSONA_LABELS[persona]}: ${options.score}/5`;
  }
  if (options.rating) {
    return PERSONA_LABELS[persona];
  }
  return `${PERSONA_LABELS[persona]} pending`;
}

/** Small persona dot + score for list rows. */
export function PersonaAvatar({
  persona,
  rating,
  score,
  skipped = false,
  size = "xs",
}: PersonaAvatarProps) {
  const dim = size === "xs" ? "h-4 w-4 text-[9px]" : "h-5 w-5 text-[10px]";
  const dotClass = rating ? PERSONA_DOT_CLASS[rating] : "bg-slate-600";
  const scoreLabel = score != null ? String(score) : "—";

  return (
    <span
      className="inline-flex items-center gap-1 text-slate-400"
      title={personaTitle(persona, { skipped, rating, score })}
      data-testid={`persona-avatar-${persona}`}
      data-skipped={skipped ? "true" : "false"}
    >
      <span
        className={clsx(
          "inline-flex shrink-0 items-center justify-center rounded-full",
          dim,
          dotClass
        )}
      >
        <span className="font-semibold text-white/90">{PERSONA_INITIALS[persona]}</span>
      </span>
      <span className="tabular-nums text-xs">{scoreLabel}</span>
    </span>
  );
}
